import { localisateurAffiche } from './citations';
// Chaîne documentaire : libellés, ordre des livrables et lecture des sources d'un champ.
//
// Ordre décidé par le fondateur le 22 octobre 2026 : DAT d'abord, dossier d'homologation ensuite, HLD et EBIOS RM après
// mesure. Aucun texte d'un livrable n'est écrit ici : l'écran affiche ce que l'API rend (faits cités, chiffres du
// moteur, raisons « à compléter » écrites par le code d'Ekera).
import type { ChampVue, CitationVue, ConstatLicenceVue, ResumeLivrable } from '../api/types';

export const ORDRE_TYPES = ['dat', 'homologation', 'hld', 'ebios_rm', 'comite', 'autre'] as const;

const AUTRE = { court: 'Autre', long: 'Autre document' };

/** Libellés d'un type de livrable (« DAT », « Dossier d’architecture technique »). */
export function libelleType(t: string): { court: string; long: string } {
  return LIBELLE_TYPE[t] ?? AUTRE;
}

export const LIBELLE_TYPE: Record<string, { court: string; long: string }> = {
  dat: { court: 'DAT', long: 'Dossier d’architecture technique' },
  homologation: { court: 'Homologation', long: 'Dossier d’homologation de sécurité' },
  hld: { court: 'HLD', long: 'Conception générale' },
  ebios_rm: { court: 'EBIOS RM', long: 'Analyse de risques EBIOS Risk Manager' },
  comite: { court: 'Comité', long: 'Dossier de comité d’architecture' },
  autre: { court: 'Autre', long: 'Autre document' },
};

export const LIBELLE_ETAT: Record<ResumeLivrable['etat'], string> = {
  a_valider: 'À valider',
  valide: 'Validé',
  publie: 'Publié',
  compensee: 'compensee', // A40 ouvert : code neutre, aucun libellé définitif décidé.
};

export const TON_ETAT: Record<ResumeLivrable['etat'], 'attention' | 'ok' | 'info'> = {
  a_valider: 'attention',
  valide: 'ok',
  publie: 'info',
  compensee: 'info',
};

export const LIBELLE_CIBLE: Record<string, string> = { sharepoint: 'SharePoint', confluence: 'Confluence' };

export function trierLivrables<T extends { type_livrable: string; titre: string }>(liste: readonly T[]): T[] {
  const rang = (t: string) => {
    const i = (ORDRE_TYPES as readonly string[]).indexOf(t);
    return i < 0 ? ORDRE_TYPES.length : i;
  };
  return [...liste].sort((a, b) => rang(a.type_livrable) - rang(b.type_livrable) || a.titre.localeCompare(b.titre, 'fr'));
}

/** Toutes les citations d'un champ : les siennes, puis celles de ses lignes (sans doublon). */
export function citationsDuChamp(c: ChampVue): CitationVue[] {
  const vues = new Set<string>();
  const out: CitationVue[] = [];
  for (const x of [...(c.citations ?? []), ...(c.lignes ?? []).flatMap((l) => l.citations ?? [])]) {
    const k = `${x.source}|${x.localisateur}|${x.extrait}`;
    if (!vues.has(k)) {
      vues.add(k);
      out.push(x);
    }
  }
  return out;
}

/** Ce que l'infobulle d'un champ dit de sa source, en une ligne (le détail est dans la marge). */
export function resumeSource(c: ChampVue): string {
  if (c.etat === 'a_completer') return `À compléter : ${c.raison_libelle ?? c.detail ?? 'aucune source ne le règle'}`;
  if (c.origine === 'moteur') return `Calcul du moteur d’Ekera${c.calcul ? ` (${c.calcul.champ})` : ''}`;
  if (c.origine === 'livrable') return 'Rempli par Ekera (date ou version du document)';
  const cits = citationsDuChamp(c);
  if (!cits.length) return 'Aucune citation';
  const premiere = cits[0]!;
  const reste = cits.length > 1 ? ` et ${cits.length - 1} autre${cits.length > 2 ? 's' : ''}` : '';
  return `Source : ${premiere.source}, ${localisateurAffiche(premiere.localisateur)} : « ${premiere.extrait.slice(0, 160)} »${reste}`;
}

export const LIBELLE_ORIGINE: Record<string, string> = {
  carte: 'Fait de la carte, cité',
  moteur: 'Calcul du moteur d’Ekera',
  livrable: 'Rempli par Ekera',
};

// ---------------------------------------------------------------- licences

export const LIBELLE_NATURE_LICENCE: Record<string, string> = {
  achetee_non_attribuee: 'Payées, non attribuées',
  compte_desactive: 'Attribuées à des comptes désactivés',
  compte_desactive_annuaire: 'Comptes désactivés dans l’annuaire, licence gardée',
  compte_inactif: 'Attribuées à des comptes inactifs',
  attribuee_au_dela_du_contrat: 'Attribuées au-delà du contrat',
};

export const LIBELLE_METHODE: Record<string, string> = {
  prix_unitaire: 'prix unitaire du contrat',
  contrat_une_reference: 'contrat d’une seule référence',
  bornes_gamme: 'bornes sûres par le rang de gamme (le contrat ne donne pas le prix de chaque référence)',
  non_calculable: 'non calculable',
};

/** Vrai pour un constat qui est une économie possible (et non un écart au contrat). */
export function estEconomie(c: ConstatLicenceVue): boolean {
  return c.nature !== 'attribuee_au_dela_du_contrat';
}
